import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import LowPriorityIcon from '../../../assets/priority_low.svg';
import HighPriorityIcon from '../../../assets/priority_high.svg';
import BackIcon from '../../../assets/back.svg';
import GlobePlaneIcon from '../../../assets/globe_plane_black.svg';
import AbroadYellowIcon from '../../../assets/abroad_yellow.svg';
import ISIcon from '../../../assets/is_black.svg';
import ISYellowIcon from '../../../assets/is_yellow.svg';
import CommentaryIcon from '../../../assets/chat_messages_black.svg';
import RightIcon from '../../../assets/arrow_right.svg';
import GetToWorkIcon from '../../../assets/get_to_work.svg';
import Postponed from '../../../assets/postponed.svg';
import { TravelClaimForList } from '../../../types/travelClaims';
import { CommentaryForm } from './CommentaryForm';
import { useOnClickOutside } from '../../../hooks/useOutsideClick';
import { useUserContext } from '../../../context/user';

interface Props {
    toggleIsImportant: (claim: TravelClaimForList) => void
    toggleIsAbroad: (claim: TravelClaimForList) => void
    toggleNonresidentColleagues: (claim: TravelClaimForList) => void
    updateComment: (claim: TravelClaimForList, text: string) => void
    returnToNew: (claim: TravelClaimForList) => void
    setInWork: (claim: TravelClaimForList) => void
    postponeClaim: (claim: TravelClaimForList) => void
}

export type TravelClaimListContextMenuHandle = {
    show: (x: number, y: number, claim: TravelClaimForList) => void;
};

export const TravelClaimListContextMenu = forwardRef<TravelClaimListContextMenuHandle,
    Props>(({
        toggleIsImportant,
        toggleIsAbroad,
        updateComment,
        returnToNew,
        postponeClaim,
        setInWork,
        toggleNonresidentColleagues,
    }: Props, ref) => {
        const [popupVisible, setPopupVisible] = useState<boolean>(false);
        const [x, setX] = useState(0);
        const [y, setY] = useState(0);
        const [claim, setClaim] = useState<TravelClaimForList>();
        const [showCommentaryForm, setShowCommentaryForm] = useState(false);
        const { userUuid } = useUserContext();

        useImperativeHandle(ref, () => ({
            show(x: number, y: number, claim: TravelClaimForList) {
                setX(x); // Не работает
                setY(y); // Не работает
                setShowCommentaryForm(false);
                setClaim(claim);
                togglePopupVisibility();
            }
        }));

        const togglePopupVisibility = () => {
            setPopupVisible(!popupVisible);
        }
        const closePopup = () => {
            setPopupVisible(false);
        }

        const wrapperRef = useRef(null);
        useOnClickOutside(wrapperRef, closePopup);

        const handleClaimAction = (action: Function) => {
            if (claim) {
                action(claim)
                togglePopupVisibility();
            }
        }

        const isCurrentUserResponsibleClaim = () => {
            return userUuid === claim?.responsible?.pk
        }

        return (
            <div
                ref={wrapperRef}
            >
                {popupVisible ?
                    showCommentaryForm ?
                        <CommentaryForm
                            claim={claim}
                            x={x}
                            y={y}
                            hide={() => setShowCommentaryForm(false)}
                            updateComment={(claim, text) => {
                                updateComment(claim, text);
                                togglePopupVisibility();
                            }}
                        />
                        :
                        <div
                            className={`absolute flex overflow-hidden justify-center z-50 bg-white rounded-lg shadow-below 
                            p-[10px]`}
                            style={{
                                left: x,
                                top: y,
                            }}
                        >
                            <div className='flex flex-col gap-1 min-w-[364px]'>
                                {claim ?
                                    <div
                                        className='p-[10px] cursor-pointer'
                                        onClick={() => handleClaimAction(toggleIsImportant)}
                                    >
                                        <img
                                            className='inline-block mr-[10px]'
                                            src={claim.important ? LowPriorityIcon : HighPriorityIcon}
                                            alt='Priority'
                                        />
                                        {claim.important ? 'Понизить приоритет' : 'Повысить приоритет'}
                                    </div>
                                    :
                                    <></>
                                }
                                {['POSTPONED', 'IN_WORK', 'FINISHED', 'ISSUED', 'REGISTERED'].includes(claim?.status || '') &&
                                    isCurrentUserResponsibleClaim() &&
                                    <div
                                        className='p-[10px] cursor-pointer'
                                        onClick={() => handleClaimAction(returnToNew)}
                                    >
                                        <img
                                            className='inline-block mr-[10px]'
                                            src={BackIcon}
                                            alt='Return to new'
                                        />
                                        Вернуть в "Новые"
                                    </div>}
                                {['IN_WORK', 'REGISTERED'].includes(claim?.status || '') &&
                                    isCurrentUserResponsibleClaim() &&
                                    <div
                                        className='p-[10px] cursor-pointer'
                                        onClick={() => handleClaimAction(postponeClaim)}
                                    >
                                        <img
                                            className='inline-block mr-[10px]'
                                            src={Postponed}
                                            alt='Postpone'
                                        />
                                        Перенести в отложенные
                                    </div>
                                }
                                {(!claim?.responsible || ['POSTPONED', 'NEW'].includes(claim?.status || '')) &&
                                    !(claim?.service === 'TRIVIO' && ['EXCHANGED', 'CANCELLED', 'ISSUED'].includes(claim?.status || '')) &&
                                    <div
                                        className='p-[10px] cursor-pointer'
                                        onClick={() => handleClaimAction(setInWork)}
                                    >
                                        <img
                                            className='inline-block mr-[10px]'
                                            src={GetToWorkIcon}
                                            alt='Set In Work'
                                        />
                                        Взять в работу
                                    </div>
                                }
                                <div
                                    className={'p-[10px] cursor-pointer ' + (claim?.is_abroad ? "text-melon-yellow" : "")}
                                    onClick={() => handleClaimAction(toggleIsAbroad)}
                                >
                                    <img
                                        className='inline-block mr-[10px]'
                                        src={claim?.is_abroad ? AbroadYellowIcon : GlobePlaneIcon}
                                        alt='International'
                                    />
                                    Международная
                                </div>
                                <div
                                    className={'p-[10px] cursor-pointer ' + (claim?.nonresident_colleagues ? "text-melon-yellow" : "")}
                                    onClick={() => handleClaimAction(toggleNonresidentColleagues)}
                                >
                                    <img
                                        className='inline-block mr-[10px]'
                                        src={claim?.nonresident_colleagues ? ISYellowIcon : ISIcon}
                                        alt='Colleagues from another city'
                                    />
                                    Иногородние сотрудники
                                </div>
                                <div
                                    className='flex bg-melon-light-grey-medium p-[10px] rounded-lg cursor-pointer'
                                    onClick={() => setShowCommentaryForm(true)}
                                >
                                    <img
                                        className='inline-block mr-[10px]'
                                        src={CommentaryIcon}
                                        alt='Commentaries'
                                    />

                                    <span className='grow'>Комментарий</span>

                                    <img
                                        className='inline-block'
                                        src={RightIcon}
                                        alt='Right'
                                    />
                                </div>
                            </div>
                        </div>
                    :
                    <></>
                }
            </div>
        );
    });
